/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * PORZĄDEK RANKINGU — jedna reguła dla wszystkich ekranów: NIEZNANE NIE JEST ZEREM
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * PO CO TO ISTNIEJE. Każdy ranking w tym produkcie prędzej czy później dostaje wiersz, dla którego
 * sortowanej wielkości NIE MA — szkoła bez rekordu po stronie Drive nie ma punktualności, danie bez
 * ceny katalogowej nie ma wartości. Naiwne `(a.x ?? 0) - (b.x ?? 0)` wrzuca taki wiersz na koniec
 * rankingu malejącego jako „najgorszy" i na początek rosnącego jako „najlepszy". W obu przypadkach
 * ekran twierdzi coś, czego nie zmierzył — a przy porządku „najsłabsza punktualność" stawia szkołę
 * BEZ POMIARU na pierwszym miejscu listy do obdzwonienia.
 *
 * Reguła jest więc jedna i nie zależy od kierunku: **wiersze bez wartości lądują na końcu**, zawsze,
 * i są tam wyłącznie dlatego, że nie wiadomo — nie dlatego, że wypadły nisko. Ekran ma to napisać
 * przy tabeli; funkcja tylko pilnuje, żeby nie dało się tego zrobić przypadkiem inaczej.
 *
 * `NaN` traktujemy jak brak. Dzielenie przez zero w metryce pochodnej daje `NaN`, a `NaN` w
 * komparatorze zwraca `false` przy każdym porównaniu, więc sortowanie robi się zależne od
 * kolejności wejściowej — czyli niepowtarzalne.
 */

/** Wartość, po której szeregujemy. `null` znaczy „nie wiadomo", nigdy „zero". */
export type Klucz<T> = (element: T) => number | null | undefined;

function wartosc<T>(klucz: Klucz<T>, x: T): number | null {
  const v = klucz(x);
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/**
 * Komparator. `kierunek: 'malejaco'` — największe najpierw (kto sprzedał najwięcej).
 * `kierunek: 'rosnaco'` — najmniejsze najpierw (kogo wziąć na warsztat).
 * W obu przypadkach brak wartości idzie NA KONIEC.
 *
 * `remis` rozstrzyga równe wartości — bez niego sort bywa stabilny tylko z przypadku, a dwa
 * odświeżenia tej samej listy potrafią pokazać inną kolejność i wyglądać jak zmiana danych.
 */
export function porzadek<T>(
  klucz: Klucz<T>,
  kierunek: 'malejaco' | 'rosnaco' = 'malejaco',
  remis?: (a: T, b: T) => number,
): (a: T, b: T) => number {
  return (a, b) => {
    const va = wartosc(klucz, a);
    const vb = wartosc(klucz, b);

    if (va === null && vb === null) return remis ? remis(a, b) : 0;
    if (va === null) return 1;
    if (vb === null) return -1;

    const roznica = kierunek === 'malejaco' ? vb - va : va - vb;
    if (roznica !== 0) return roznica;
    return remis ? remis(a, b) : 0;
  };
}

/** Ile wierszy nie ma wartości sortowanej. Ekran podaje tę liczbę przy tabeli. */
export function bezWartosci<T>(lista: readonly T[], klucz: Klucz<T>): number {
  return lista.reduce((n, x) => n + (wartosc(klucz, x) === null ? 1 : 0), 0);
}
